import { Injectable, ResourceRef, Signal, computed, effect, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { timer, switchMap, map, of, takeWhile } from 'rxjs';
import { RestApiService } from '../../core/services/rest-api.service';
import { LocalStorageKeys } from '@shared/constants/local-storage-keys';
import {
  ActiveVippsUIStatus,
  GetVippsPaymentResp,
  VippsPaymentApiStatus,
} from '@shared/interfaces/vipps';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { PurchaseSuccessDialogService } from '@shared/components/purchase-success-dialog/purchase-success-dialog.service';

@Injectable({ providedIn: 'root' })
export class PaymentRefPollingService {
  private api = inject(RestApiService);
  private snackbar = inject(DvSnackbarService);
  private translate = inject(TranslateService);
  private purchaseSuccessDialog = inject(PurchaseSuccessDialogService);

  private readonly StorageKey = LocalStorageKeys.RefId;
  private readonly TerminalStates: VippsPaymentApiStatus[] = [
    'ABORTED',
    'EXPIRED',
    'TERMINATED',
    'AUTHORIZED',
  ];

  readonly routeNetworkId = computed(() => this.refKey()?.split('.')[0] ?? null);
  readonly refId = computed(() => this.refKey()?.split('.')[1] ?? null);

  private readonly refKey = signal<string | null>(localStorage.getItem(this.StorageKey));

  readonly paymentStatus: Signal<ActiveVippsUIStatus> = computed(() => {
    const status = this.statusResource.value();
    if (status !== 'CREATED' && status !== 'WAITING') return undefined;
    return status;
  });

  private readonly statusResource: ResourceRef<VippsPaymentApiStatus | undefined> = rxResource({
    defaultValue: 'WAITING',
    params: () => {
      return { id: this.refKey()?.split('.')?.[1] };
    },
    stream: ({ params }) => {
      if (!params.id) return of(undefined);

      return timer(0, 2000).pipe(
        switchMap(() =>
          this.api
            .get<GetVippsPaymentResp>(`vipps/payment/${params.id}`)
            .pipe(map((resp) => resp.state as VippsPaymentApiStatus)),
        ),
        takeWhile((state) => !this.TerminalStates.includes(state), true),
      );
    },
  });

  constructor() {
    effect(() => {
      const refKey = this.refKey();
      if (refKey) {
        localStorage.setItem(this.StorageKey, refKey);
      }
    });

    effect(() => {
      const state = this.statusResource.value();

      if (this.TerminalStates.includes(state)) {
        localStorage.removeItem(this.StorageKey);
      }
    });

    effect(() => {
      const status = this.statusResource.value();

      if (status !== 'CREATED' && status !== 'WAITING') {
        if (status === 'EXPIRED') {
          this.snackbar.open(
            this.translate.instant('Vipps.PaymentExpired'),
            SnackBarConfig.Warning,
          );
        }
        if (status === 'AUTHORIZED') {
          this.purchaseSuccessDialog.openPurchaseSuccessDialog();
        }
        return undefined;
      }

      return status;
    });
  }

  start(refId: string, routeNetworkId: string): void {
    this.refKey.set(`${routeNetworkId}.${refId}`);
  }

  stop(): void {
    this.refKey.set(null);
  }
}
